import React from 'react';
import { useNavigate, useSubmit, useActionData, useLoaderData, redirect } from 'react-router-dom';
import { AuthProvider } from '../../auth_provider';
import { ContentFrame } from '../../components/content_frame/content_frame';
import { getModel, getModels} from '../../utils/functions';
import { 
  DESKTOP_PATH, 
  COMPLAINT_TYPE_LIST_PATH, 
  COMPLAINT_TYPE_ADD_PATH, 
  COMPLAINT_TYPE_EDIT_PATH,
  LOGIN_PATH } from '../../constants/paths';
import { COMPLAINT_TYPE_GET_API, DOCUMENTS_GET_API } from '../../constants/apis';
import { ComplaintTypeData } from './complaint_type_data';


const DEFAULT_COMPLAINT_TYPE = {
  name: "",
  description: "",
  critical_threshold: 50.0,
  moderate_threshold: 75.0,
  light_threshold: 90.0,
  very_light_threshold: 100.0,
  document_configurations: []
};

export async function complaintTypeAddLoader() {
  const complaint_type = DEFAULT_COMPLAINT_TYPE;

  try {
    const documents = await getModels(DOCUMENTS_GET_API);

    complaint_type.document_configurations = documents.map((document) => {
      return {
        document_name: document.name,
        document_id: document.id,
        score_when_present: 0,
        score_when_absent: 0,
        is_relevant: false
      };
    });

    return {
      complaint_type,
      documents,
      riskRange: [0, 0]
    };
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

export async function complaintTypeEditLoader({ params }) {
  try {
    let complaint_type;
    let documents;

    if (AuthProvider.is_authenticated) {
      complaint_type = getModel(`${COMPLAINT_TYPE_GET_API}${params.complaint_type_id}`);
      documents = getModels(DOCUMENTS_GET_API);
  
      complaint_type = await complaint_type;
      documents = await documents;
    }
    else {
      complaint_type = await getModel(`${COMPLAINT_TYPE_GET_API}${params.complaint_type_id}`);
      documents = await getModels(DOCUMENTS_GET_API);
    }

    let min = 0;
    let max = 0;

    complaint_type.document_configurations = documents.map((document) => {
      let association = complaint_type.document_configurations.find((entry) => entry.document_id === document.id);

      if (!association) {
        association = {
          document_id: document.id,
          score_when_present: 0,
          score_when_absent: 0,
          is_relevant: false
        };
      }
      
      association.document_name = document.name;

      if (association.is_relevant) {
        min += Math.min(association.score_when_absent, association.score_when_present);
        max += Math.max(association.score_when_absent, association.score_when_present);        
      }

      return association;
    });

    return {
      complaint_type,
      documents,
      riskRange: [min, max]
    };
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};


class ComplaintType extends React.Component {
  constructor(props) {
    super(props);
    
    this.state = {
      complaint_type: {
        ...this.props.complaint_type
      },
      riskRange: [...this.props.riskRange]
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.complaint_type !== this.props.complaint_type) {
      this.setState({
        complaint_type: {
          ...this.props.complaint_type
        },
        riskRange: [...this.props.riskRange]
      });
    }
  }

  handleInputChange(event) {
    const target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    const update = {
      complaint_type: {...this.state.complaint_type}
    };

    if(name.startsWith('document_configurations:')) {
      const selection = name.split(':');
      const document_id = parseInt(selection[1]);
      const property = selection[2];

      let min = 0;
      let max = 0;

      update.complaint_type.document_configurations = update.complaint_type.document_configurations.map((entry) => {
        const data = {...entry};

        if (data.document_id === document_id) {
          if (value.length > 0 && !isNaN(value)) {
            value = parseFloat(value);
          }
          
          data[property] = value;
        }

        if (typeof data.score_when_present === "string" || typeof data.score_when_absent === "string") {
          min = null;
          max = null;
        }
        else if (min !== null && max !== null && data.is_relevant) {
          min += Math.min(data.score_when_absent, data.score_when_present);
          max += Math.max(data.score_when_absent, data.score_when_present);
        }

        return data;
      });

      update.riskRange = [min, max];
    }
    else {
      if (name === 'critical_threshold' || 
          name === 'moderate_threshold' || 
          name === 'light_threshold' || 
          name === 'very_light_threshold') {
        if (value.length > 0 && !isNaN(value)) {
          value = parseFloat(value);
        }
      }

      update.complaint_type[name] = value;  
    }

    this.setState(update);
  }

  onToggleIsRelevant(document_id) {
    const complaint_type = {...this.state.complaint_type};
    
    let min = 0;
    let max = 0;

    complaint_type.document_configurations = complaint_type.document_configurations.map((entry) => {
      const data = {...entry};

      if (data.document_id === document_id) {
        data.is_relevant = !data.is_relevant;

        if (!data.is_relevant) {
          if (typeof data.score_when_present === "string") {
            data.score_when_present = 0;
          }
          if (typeof data.score_when_absent === "string") {
            data.score_when_absent = 0;
          }
        }
      }
      
      if (data.is_relevant) {
        min += Math.min(data.score_when_absent, data.score_when_present);
        max += Math.max(data.score_when_absent, data.score_when_present);
      }

      return data;
    });

    this.setState({
      complaint_type,
      riskRange: [min, max]
    });
  }

  inputsAreValid() {    
    return this.state.complaint_type.name.length > 0 &&
           this.state.complaint_type.critical_threshold !== null &&
           this.state.complaint_type.moderate_threshold !== null &&
           this.state.complaint_type.light_threshold !== null &&
           this.state.complaint_type.very_light_threshold !== null &&
           this.state.complaint_type.document_configurations.every((entry) => typeof entry.score_when_present !== "string" && typeof entry.score_when_absent !== "string");
  }

  clearWarningMessage() {
    this.props.submit(
      {resetWarnings: true}, 
      {
        method: 'POST',
        encType: "application/json"
      });
  }

  async saveData() {
    let data = {...this.state.complaint_type};
    
    this.props.submit(
      {data}, 
      {
        method: this.props.isEdit ? 'PATCH' : 'POST',
        encType: "application/json"
      });
  }

  render() {
    return (
      <ContentFrame
        headerHistory={[
          {
            path: DESKTOP_PATH,
            text: "Área de trabalho"
          },
          {
            path: COMPLAINT_TYPE_LIST_PATH,
            text: "Listar queixas"
          },
          {
            path: this.props.isEdit ? COMPLAINT_TYPE_EDIT_PATH : COMPLAINT_TYPE_ADD_PATH,
            text: this.props.isEdit ? 'Editar queixa' : 'Adicionar queixa'
          },
        ]}
        titleIcon={this.props.isEdit ? <i className="fas fa-edit"></i> : <i className="fas fa-plus"></i>}
        title={this.props.isEdit ? 'Editar queixa' : 'Adicionar queixa'}
      >

        <ComplaintTypeData
          warningMessage={this.props.warningMessage}
          showWarningMessage={this.props.warningMessage.length > 0}
          complaint_type={this.state.complaint_type}
          documents={this.props.documents}
          riskRange={this.state.riskRange}
          onSave={() => this.saveData()}
          onCloseWarning={() => this.clearWarningMessage()}
          enableSave={this.inputsAreValid()}
          handleInputChange={(event) => this.handleInputChange(event)}
          onToggleIsRelevant={(document_id) => this.onToggleIsRelevant(document_id)}
          highlights={this.props.parametersToHighlight}
          onCancelPath={COMPLAINT_TYPE_LIST_PATH}
        />

      </ContentFrame>
    );
  }
};

const WrappedComplaintType = props => {
  const { complaint_type, documents, riskRange } = useLoaderData();
  const navigate = useNavigate();
  const submit = useSubmit();
  const actionData = useActionData();

  let warningMessage = '';
  let parametersToHighlight = [];

  if (actionData) {
    warningMessage = actionData.message || '';
    parametersToHighlight = actionData.parameters || [];
  }  

  return (
    <ComplaintType
      navigate={navigate}
      submit={submit}
      warningMessage={warningMessage}
      parametersToHighlight={parametersToHighlight}
      complaint_type={complaint_type}
      documents={documents}
      riskRange={riskRange}
      {...props}
    />
  );
};

export { WrappedComplaintType as ComplaintType};
